import { faDiagramNested, faEdit } from "@fortawesome/pro-regular-svg-icons";
import { Root as AccordionRoot } from "@radix-ui/react-accordion";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, UseFormReturn, useForm } from "react-hook-form";

import {
  FlowToolConfigDrawerData,
  SideDrawer,
  useSideDrawerCoordinator,
} from "src/agents/base/SideDrawer";
import { useAgentVersion } from "src/agents/queries/management";
import { Schema, SchemaTypes } from "src/api/flowTypes";
import { useFlowBySlug } from "src/api/queries";
import { Button } from "src/base-components/Button";
import {
  accordionRootClassName,
  EditorAccordionItem,
} from "src/base-components/EditorAccordionItem";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { TabData, Tabs } from "src/base-components/Tabs";
import { Textarea } from "src/base-components/Textarea";
import { SCHEMA_TYPE_ICONS } from "src/base-components/TypeIcons";
import { MetadataOverlay } from "src/clients/agent-management";
import { FlowVersionStatus } from "src/clients/flow-api";
import { Callout } from "src/design-system/Callout";
import { VersionStatusPill } from "src/flow/Pills";
import { UserNameInfo } from "src/flowContainer/versionEditedInfo/UsernameInfo";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { AgentVersionPageParams } from "src/router/urls";
import { formatDate } from "src/utils/datetime";
import { useParamsDecode } from "src/utils/useParamsDecode";

// Accordion section IDs
const SECTION_FLOW = "flow";
const SECTION_BASIC = "basic";
const SECTION_SCHEMA = "schema";

type FlowToolFormValues = {
  name: string;
  description: string;
  inputMetadataOverlay: MetadataOverlay;
  outputMetadataOverlay: MetadataOverlay;
};

export const FlowToolConfigDrawer: React.FC = () => {
  const { openDrawer } = useSideDrawerCoordinator();
  const data =
    openDrawer?.type === "flowToolConfig" ? openDrawer.data : undefined;

  // To not make things overly complex, we fetch the agent version here
  // to get a fresh tool name
  const { agentId, versionId } = useParamsDecode<AgentVersionPageParams>();
  const agentVersionQuery = useAgentVersion(agentId, versionId);

  const toolName =
    (agentVersionQuery.data &&
      data?.toolIndex !== undefined &&
      agentVersionQuery.data.configuration.tools?.[data.toolIndex]?.name) ||
    "Tool";

  return (
    <SideDrawer
      dataLoc="flow-tool-config-drawer"
      drawerId="flowToolConfig"
      header={{
        title: toolName,
        description: "Decision Flow Tool",
        closeDataLoc: "close-flow-tool-config-drawer",
        reverseTitleAndDescription: true,
        icon: (
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-green-electric/40">
            <Icon color="text-gray-800" icon={faDiagramNested} size="sm" />
          </div>
        ),
      }}
    >
      {data && <FlowToolConfigDrawerContent {...data} />}
    </SideDrawer>
  );
};

const FlowToolConfigDrawerContent: React.FC<FlowToolConfigDrawerData> = ({
  tool,
  onToolChange,
  onRequestChangeFlow,
  immutable,
}) => {
  // Fetch flow data for display
  const { workspace } = useWorkspaceContext();
  const flowQuery = useFlowBySlug(workspace.id, tool.flow_slug, {
    refetchOnMount: true,
  });
  const flow = flowQuery.data;
  const version = flow?.versions.find((v) => v.id === tool.flow_version_id);

  // Local form for all editable fields
  const localForm = useForm<FlowToolFormValues>({
    defaultValues: {
      name: tool.name,
      description: tool.description ?? "",
      inputMetadataOverlay: tool.input_schema_metadata_overlay ?? {
        properties: {},
      },
      outputMetadataOverlay: tool.output_schema_metadata_overlay ?? {
        properties: {},
      },
    },
    mode: "onChange",
  });

  // Accordion state
  const [expandedSections, setExpandedSections] = useState<string[]>([
    SECTION_FLOW,
    SECTION_BASIC,
  ]);

  // Reset form when flow version changes (e.g., user selected different flow/version)
  useEffect(() => {
    localForm.reset({
      name: tool.name,
      description: tool.description ?? "",
      inputMetadataOverlay: tool.input_schema_metadata_overlay ?? {
        properties: {},
      },
      outputMetadataOverlay: tool.output_schema_metadata_overlay ?? {
        properties: {},
      },
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool.flow_version_id, localForm]);

  // Sync overlays when version schema changes (add missing properties, remove stale ones)
  useEffect(() => {
    if (!version) return;

    const syncOverlay = (
      schema: Schema | undefined,
      existingOverlay: MetadataOverlay,
    ): MetadataOverlay => {
      const schemaProps = schema?.properties ?? {};
      const overlayProps = existingOverlay.properties ?? {};

      // Check if in sync (same keys)
      const schemaKeys = new Set(Object.keys(schemaProps));
      const overlayKeys = new Set(Object.keys(overlayProps));
      const isInSync =
        schemaKeys.size === overlayKeys.size &&
        [...schemaKeys].every((k) => overlayKeys.has(k));

      if (isInSync) return existingOverlay; // No change needed

      // Rebuild overlay with only current schema properties
      const newOverlay: MetadataOverlay = { properties: {} };
      for (const propName of Object.keys(schemaProps)) {
        newOverlay.properties[propName] = {
          description: overlayProps[propName]?.description ?? "",
        };
      }
      return newOverlay;
    };

    const currentValues = localForm.getValues();
    const newInputOverlay = syncOverlay(
      version.input_schema,
      currentValues.inputMetadataOverlay,
    );
    const newOutputOverlay = syncOverlay(
      version.output_schema,
      currentValues.outputMetadataOverlay,
    );

    localForm.setValue("inputMetadataOverlay", newInputOverlay);
    localForm.setValue("outputMetadataOverlay", newOutputOverlay);
  }, [version, localForm]);

  // Subscribe to form changes and propagate to parent
  useEffect(
    () =>
      localForm.subscribe({
        formState: { values: true },
        callback: async () => {
          const isValid = await localForm.trigger();
          if (!isValid) return;

          const values = localForm.getValues();
          onToolChange({
            ...tool,
            name: values.name,
            description: values.description || undefined,
            input_schema_metadata_overlay: values.inputMetadataOverlay,
            output_schema_metadata_overlay: values.outputMetadataOverlay,
          });
        },
      }),
    [localForm, tool, onToolChange],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="decideScrollbar flex-1 overflow-y-auto p-5">
        <AccordionRoot
          className={accordionRootClassName}
          type="multiple"
          value={expandedSections}
          onValueChange={setExpandedSections}
        >
          {/* Decision flow section */}
          <EditorAccordionItem
            dataLoc="tool-flow-section"
            title="Decision Flow"
            value={SECTION_FLOW}
          >
            <FlowInfoCard
              flow={flow}
              immutable={immutable}
              version={version}
              onEdit={onRequestChangeFlow}
            />
          </EditorAccordionItem>

          {/* Basic details section */}
          <EditorAccordionItem
            dataLoc="tool-basic-details-section"
            title="Basic details"
            value={SECTION_BASIC}
          >
            <div className="mb-4 rounded-lg bg-gray-50 p-4">
              <div className="flex flex-col gap-4">
                <div>
                  <label className="mb-1.5 block text-gray-800 font-inter-semibold-13px">
                    Name{" "}
                    <span className="text-indigo-500 font-inter-normal-12px">
                      *
                    </span>
                  </label>
                  <Controller
                    control={localForm.control}
                    name="name"
                    render={({ field, fieldState }) => (
                      <Input
                        data-loc="tool-name-input"
                        disabled={immutable}
                        errored={!!fieldState.error}
                        placeholder="Enter tool name"
                        value={field.value}
                        fullWidth
                        onChange={field.onChange}
                      />
                    )}
                    rules={{ required: "Name is required" }}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-gray-800 font-inter-semibold-13px">
                    Description{" "}
                  </label>
                  <Controller
                    control={localForm.control}
                    name="description"
                    render={({ field }) => (
                      <Textarea
                        data-loc="tool-description-input"
                        disabled={immutable}
                        placeholder="Enter tool description"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </EditorAccordionItem>

          {/* Schema section */}
          <EditorAccordionItem
            dataLoc="tool-schema-section"
            title="Schema"
            value={SECTION_SCHEMA}
          >
            {version ? (
              <FlowToolSchemaViewer
                flowVersionName={version.name}
                form={localForm}
                immutable={immutable}
                inputSchema={version.input_schema}
                outputSchema={version.output_schema}
              />
            ) : (
              <div className="mb-4 rounded-lg bg-gray-50 p-4">
                <div className="space-y-3">
                  <SkeletonPlaceholder height="h-4" width="w-full" />
                  <SkeletonPlaceholder height="h-8" width="w-24" />
                  <SkeletonPlaceholder height="h-20" width="w-full" />
                </div>
              </div>
            )}
          </EditorAccordionItem>
        </AccordionRoot>
      </div>
    </div>
  );
};

// ============================================================================
// Flow Info Card Component
// ============================================================================

type FlowInfoCardProps = {
  flow?: { id: string; name: string; meta: { description?: string } };
  version?: {
    id: string;
    name: string;
    status: FlowVersionStatus;
    updated_at: string;
    meta?: {
      release_note?: string;
      published_at?: string;
      published_by_id?: string;
      updated_by_id?: string;
      created_by_id?: string;
    };
  };
  immutable: boolean;
  onEdit: () => void;
};

const FlowInfoCard: React.FC<FlowInfoCardProps> = ({
  flow,
  version,
  immutable,
  onEdit,
}) => {
  const isPublished = version?.status === FlowVersionStatus.PUBLISHED;

  if (!flow || !version) {
    return (
      <div className="mb-4 rounded-lg bg-gray-50 p-4">
        <div className="space-y-3">
          <SkeletonPlaceholder height="h-4" width="w-48" />
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="space-y-2">
              <SkeletonPlaceholder height="h-5" width="w-40" />
              <SkeletonPlaceholder height="h-4" width="w-full" />
              <SkeletonPlaceholder height="h-4" width="w-56" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const date = isPublished ? version.meta?.published_at : version.updated_at;
  const userId = isPublished
    ? version.meta?.published_by_id
    : version.meta?.updated_by_id || version.meta?.created_by_id;
  const formattedDate = date ? formatDate(date) : null;

  return (
    <div className="mb-4 rounded-lg bg-gray-50 p-4">
      <div className="space-y-3">
        <div className="text-gray-800 font-inter-semibold-13px">
          Select Decision flow and version{" "}
          <span className="text-indigo-500 font-inter-normal-12px">*</span>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <span className="text-gray-800 font-inter-semibold-13px">
                {flow.name}, {version.name}
              </span>
              <p className="mt-1 text-gray-500 font-inter-normal-12px">
                {version.meta?.release_note ||
                  flow.meta.description ||
                  "No description"}
              </p>
              {userId && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-gray-600 font-inter-normal-12px">
                  {isPublished ? "Published by" : "Last edited by"}
                  <span className="flex items-center !text-gray-800 !font-inter-medium-12px">
                    <UserNameInfo userId={userId} withAvatar />
                  </span>
                  {formattedDate && (
                    <>
                      on{" "}
                      <span className="text-gray-800 font-inter-medium-12px">
                        {formattedDate}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
            <VersionStatusPill status={version.status} />
          </div>
        </div>
        {!immutable && (
          <Button
            dataLoc="edit-flow-selection-button"
            iconLeft={faEdit}
            size="sm"
            variant="secondary"
            onClick={onEdit}
          >
            Edit
          </Button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Flow Tool Schema Viewer Component
// ============================================================================

type FlowToolSchemaViewerProps = {
  inputSchema?: Schema;
  outputSchema?: Schema;
  form: UseFormReturn<FlowToolFormValues>;
  flowVersionName?: string;
  immutable: boolean;
};

type SchemaTab = "input" | "output";

const SCHEMA_TYPE_OPTIONS = [
  { key: "string", value: "String", icon: SCHEMA_TYPE_ICONS.string },
  { key: "number", value: "Number", icon: SCHEMA_TYPE_ICONS.number },
  { key: "boolean", value: "Boolean", icon: SCHEMA_TYPE_ICONS.boolean },
  { key: "integer", value: "Integer", icon: SCHEMA_TYPE_ICONS.integer },
  { key: "array", value: "Array", icon: SCHEMA_TYPE_ICONS.array },
  { key: "object", value: "Object", icon: SCHEMA_TYPE_ICONS.object },
] as const;

const getSchemaTypeOption = (type: string | string[] | unknown) => {
  // Handle array types like ["string", "null"] for nullable
  const primaryType = Array.isArray(type) ? type[0] : type;
  const typeStr = typeof primaryType === "string" ? primaryType : "string";
  const normalized = typeStr.toLowerCase() as SchemaTypes;
  return (
    SCHEMA_TYPE_OPTIONS.find((opt) => opt.key === normalized) ?? {
      key: typeStr,
      value: typeStr.charAt(0).toUpperCase() + typeStr.slice(1),
      icon: SCHEMA_TYPE_ICONS.string,
    }
  );
};

const FlowToolSchemaViewer: React.FC<FlowToolSchemaViewerProps> = ({
  inputSchema,
  outputSchema,
  form,
  flowVersionName,
  immutable,
}) => {
  const [activeTab, setActiveTab] = useState<SchemaTab>("input");

  const tabs: TabData[] = useMemo(
    () => [
      {
        key: "input",
        label: "Input",
        content: (
          <SchemaPropertiesPanel
            form={form}
            immutable={immutable}
            overlayFieldName="inputMetadataOverlay"
            schema={inputSchema}
          />
        ),
        unmount: false,
      },
      {
        key: "output",
        label: "Output",
        content: (
          <SchemaPropertiesPanel
            form={form}
            immutable={immutable}
            overlayFieldName="outputMetadataOverlay"
            schema={outputSchema}
          />
        ),
        unmount: false,
      },
    ],
    [inputSchema, outputSchema, form, immutable],
  );

  return (
    <div>
      <p className="mb-2 text-gray-500 font-inter-normal-12px">
        Configure the input and output fields for this tool
      </p>

      {flowVersionName && (
        <Callout type="info">
          The input schema below is derived from "{flowVersionName}". You can
          add descriptions but cannot change the structure.
        </Callout>
      )}
      <div className="my-4 rounded-lg bg-gray-50 p-4">
        <Tabs
          selectedKey={activeTab}
          tabClassName="px-0 pr-4"
          tabListClassName="flex border-b border-gray-200"
          tabs={tabs}
          onChange={(key) => setActiveTab(key as SchemaTab)}
        />
      </div>
    </div>
  );
};

type SchemaPropertiesPanelProps = {
  schema?: Schema;
  overlayFieldName: "inputMetadataOverlay" | "outputMetadataOverlay";
  form: UseFormReturn<FlowToolFormValues>;
  immutable: boolean;
};

const SchemaPropertiesPanel: React.FC<SchemaPropertiesPanelProps> = ({
  schema,
  overlayFieldName,
  form,
  immutable,
}) => {
  const properties = schema?.properties ?? {};
  const requiredFields = schema?.required ?? [];

  if (Object.keys(properties).length === 0) {
    return (
      <p className="mt-3 text-gray-400 font-inter-normal-12px">
        No properties defined
      </p>
    );
  }

  return (
    <div className="mt-3">
      {/* Column headers */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <span className="text-gray-500 font-inter-normal-12px">Name</span>
        </div>
        <div className="w-32">
          <span className="text-gray-500 font-inter-normal-12px">Type</span>
        </div>
      </div>
      <div className="mt-2 space-y-4">
        {/* Schema properties */}
        {Object.entries(properties).map(([propName, propSchema]) => {
          const typedSchema = propSchema as {
            type?: string;
            description?: string;
          };
          const isRequired = requiredFields.includes(propName);
          const displayName = isRequired ? `${propName}*` : propName;
          const typeValue = typedSchema.type ?? "string";
          const typeOption = getSchemaTypeOption(typeValue);
          const fieldName =
            `${overlayFieldName}.properties.${propName}.description` as const;

          return (
            <div key={propName} className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Input value={displayName} disabled fullWidth />
                </div>
                <div className="w-32">
                  <FixedSimpleDropdown
                    elements={[typeOption]}
                    placeholder="Type"
                    selectedKey={typeOption.key}
                    disabled
                    onSelect={() => {}}
                  />
                </div>
              </div>
              <Controller
                control={form.control}
                name={fieldName}
                render={({ field }) => (
                  <Textarea
                    disabled={immutable}
                    placeholder={
                      typedSchema.description || "Add description..."
                    }
                    value={field.value ?? ""}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
